// axios配置  可自行根据项目进行更改，只需更改该文件即可，其他文件可以不动
// The axios configuration can be changed according to the project, just change the file, other files can be left unchanged

import type { AxiosInstance, AxiosResponse } from 'axios';
import { clone } from 'lodash-es';
import type { RequestOptions, Result } from '#/axios';
import type { AxiosTransform, CreateAxiosOptions } from './axiosTransform';
import { VAxios } from './Axios';
import { checkStatus } from './checkStatus';
import { useGlobSetting } from '@/hooks/setting';
import { useMessage } from '@/hooks/web/useMessage';
import { RequestEnum, ResultEnum, ContentTypeEnum } from '@/enums/httpEnum';
import { isString, isUndefined, isNull, isEmpty } from '../is';

import { getToken, setToken } from '@/utils/auth';
import { setObjToUrlParams, deepMerge } from '@/utils';

// import { useI18n } from 'vue-i18n'
import { joinTimestamp, formatRequestDate } from './helper';

// import { useErrorLogStoreWithOut } from '@/store/modules/errorLog'
// import { useUserStoreWithOut } from '@/store/modules/user'
// import { AxiosRetry } from './axiosRetry'
import axios from 'axios';
import { useI18n } from '@/hooks/web/useI18n';
import { MessagePlugin } from 'tdesign-vue-next';
import { getGlobalHeaderLocale } from '@/locales/setup';

const globSetting = useGlobSetting();
const { t } = useI18n();
const urlPrefix = globSetting.urlPrefix;

const { createMessage, createErrorModal, createSuccessModal } = useMessage();

/**
 * @description: 数据处理，方便区分多种处理方式
 */
const transform: AxiosTransform = {
  /**
   * @description: 处理响应数据。如果数据不是预期格式，可直接抛出错误
   */
  transformResponseHook: (res: AxiosResponse<Result>, options: RequestOptions) => {
    const { isTransformResponse, isReturnNativeResponse } = options;
    // 是否返回原生响应头 比如：需要获取响应头时使用该属性
    if (isReturnNativeResponse) {
      return res;
    }
    // 不进行任何处理，直接返回
    // 用于页面代码可能需要直接获取code，data，message这些信息时开启
    if (!isTransformResponse) {
      return res.data;
    }
    // 错误的时候返回
    const { data } = res;
    if (!data) {
      // return '[HTTP] Request has no return value';
      throw new Error(t('sys.api.apiRequestFailed'));
    }
    //  这里 code，result，message为 后台统一的字段，需要在 types.ts内修改为项目自己的接口返回格式
    const { code, result, message } = data;
    const _code = Number(code);
    // 这里逻辑可以根据项目进行修改
    const hasSuccess = data && Reflect.has(data, 'code') && _code === ResultEnum.SUCCESS;
    if (hasSuccess) {
      let successMsg = message;

      if (isNull(successMsg) || isUndefined(successMsg) || isEmpty(successMsg)) {
        successMsg = t(`sys.api.operationSuccess`);
      }

      if (options.successMessageMode === 'modal') {
        createSuccessModal({ title: t('sys.api.successTip'), content: successMsg });
      } else if (options.successMessageMode === 'message') {
        createMessage.success(successMsg);
      }
      return result;
    }

    // 在此处根据自己项目的实际情况对不同的code执行不同的操作
    // 如果不希望中断当前请求，请return数据，否则直接抛出异常即可
    let timeoutMsg = '';
    switch (_code) {
      case ResultEnum.TIMEOUT:
        timeoutMsg = t('sys.api.timeoutMessage');
        // TODO： 注意登出逻辑
        // const userStore = useUserStoreWithOut()
        // // 被动登出，带redirect地址
        // userStore.logout(false)
        break;
      default:
        if (message) {
          timeoutMsg = message;
        }
    }

    // errorMessageMode='modal'的时候会显示modal错误弹窗，而不是消息提示，用于一些比较重要的错误
    // errorMessageMode='none' 一般是调用时明确表示不希望自动弹出错误提示
    if (options.errorMessageMode === 'modal') {
      createErrorModal({ title: t('sys.api.errorTip'), content: timeoutMsg });
    } else if (options.errorMessageMode === 'message') {
      createMessage.error(timeoutMsg);
    }

    throw new Error(timeoutMsg || t('sys.api.apiRequestFailed'));
  },

  // 请求之前处理config
  beforeRequestHook: (config, options) => {
    const { apiUrl, joinPrefix, joinParamsToUrl, formatDate, joinTime = true, urlPrefix } = options;

    if (joinPrefix) {
      config.url = `${urlPrefix}${config.url}`;
    }

    if (apiUrl && isString(apiUrl)) {
      config.url = `${apiUrl}${config.url}`;
    }
    const params = config.params || {};
    const data = config.data || false;
    formatDate && data && !isString(data) && formatRequestDate(data);
    if (config.method?.toUpperCase() === RequestEnum.GET) {
      if (!isString(params)) {
        // 给 get 请求加上时间戳参数，避免从缓存中拿数据。
        config.params = Object.assign(params || {}, joinTimestamp(joinTime, false));
      } else {
        // 兼容restful风格
        config.url = config.url + params + `${joinTimestamp(joinTime, true)}`;
        config.params = undefined;
      }
    } else {
      if (!isString(params)) {
        formatDate && formatRequestDate(params);
        if (
          Reflect.has(config, 'data') &&
          config.data &&
          (Object.keys(config.data).length > 0 || config.data instanceof FormData)
        ) {
          config.data = data;
          config.params = params;
        } else {
          // 非GET请求如果没有提供data，则将params视为data
          config.data = params;
          config.params = undefined;
        }
        if (joinParamsToUrl) {
          config.url = setObjToUrlParams(
            config.url as string,
            Object.assign({}, config.params, config.data)
          );
        }
      } else {
        // 兼容restful风格
        config.url = config.url + params;
        config.params = undefined;
      }
    }
    return config;
  },

  /**
   * @description: 请求拦截器处理
   */
  requestInterceptors: (config, options) => {
    // 请求之前处理config
    const token = getToken(options.onlineToken ?? false);
    if (token && (config as Recordable)?.requestOptions?.withToken !== false) {
      // jwt token
      (config as Recordable).headers.Authorization = options.authenticationScheme
        ? `${options.authenticationScheme} ${token}`
        : token;
    }
    (config as Recordable).headers['Accept-Language'] = getGlobalHeaderLocale();
    return config;
  },

  /**
   * @description: 响应拦截器处理
   */
  responseInterceptors: (res: AxiosResponse<any>) => {
    const headers = res.headers;
    const { onlineToken } = res.config as any;
    // 如果是云端调用，则将 headers 中的 New-Online-Token 的赋值给云端 token
    if (headers['New-Online-Token'] && onlineToken === true) {
      setToken(headers['New-Online-Token']);
    }
    return res;
  },

  /**
   * @description: 响应错误处理
   */
  responseInterceptorsCatch: (axiosInstance: AxiosInstance, error: any) => {
    // TODO: 错误异常处理
    const { response, config, code } = error || {};
    const errorMessageMode = config?.requestOptions?.errorMessageMode || 'none';
    const msg: string = response?.data?.message ?? '';
    // console.log('-=-===--==-', error)
    // 云端接口处理网络提示
    if (isOnlineUrl(config?.url) && isAxiosNetError(code)) {
      MessagePlugin.warning(t('login.tipNetNotAvailable'));
      return Promise.reject(error);
    }
    // 提示检查本地Java服务
    if (!isOnlineUrl(config?.url) && isAxiosNetError(code)) {
      if (
        config?.url &&
        (config?.url.includes('/api/software/version/check') ||
          config?.url.includes('/api/software/version/upgradeCheck') ||
          config?.url.includes('/api/edge/user/checkState'))
      ) {
        console.log('本地服务未启动');
      } else {
        if (
          code &&
          code == 'ERR_NETWORK' &&
          !response &&
          config?.url &&
          config?.url.includes('http://127.0.0.1:5000/api/reader')
        ) {
          console.log('Agent未启动');
          MessagePlugin.warning(t('icCard.agentIsNoStarted'));
        } else {
          MessagePlugin.warning(t('login.tipWaitLocalService'));
        }
      }
      return Promise.reject(error);
    }
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    checkStatus(error?.response?.status, msg, errorMessageMode, !isOnlineUrl(config?.url));

    return Promise.reject(error);
  }
};

export function createAxios(opt?: Partial<CreateAxiosOptions>) {
  return new VAxios(
    // 深度合并
    deepMerge(
      {
        // See https://developer.mozilla.org/en-US/docs/Web/HTTP/Authentication#authentication_schemes
        // authentication schemes，e.g: Bearer
        // authenticationScheme: 'Bearer',
        authenticationScheme: 'Bearer',
        // 判断读取 token 读取边端还是云端
        onlineToken: false,
        // 默认加上时间戳
        timeout: 300 * 1000,
        // 基础接口地址
        headers: { 'Content-Type': ContentTypeEnum.JSON },
        // 如果是form-data格式
        // headers: { 'Content-Type': ContentTypeEnum.FORM_URLENCODED },
        // 数据处理方式
        transform: clone(transform),
        // 配置项，下面的选项都可以在独立的接口请求中覆盖
        requestOptions: {
          // 默认将prefix 添加到url
          // TODO: 是否需要添加到url
          joinPrefix: false,
          // 是否返回原生响应头 比如：需要获取响应头时使用该属性
          isReturnNativeResponse: false,
          // 需要对返回数据进行处理
          isTransformResponse: false,
          // post请求的时候添加参数到url
          joinParamsToUrl: false,
          // 格式化提交参数时间
          formatDate: true,
          // 消息提示类型
          errorMessageMode: 'message',
          // 接口地址
          apiUrl: globSetting.apiUrl,
          // 接口拼接地址
          urlPrefix: urlPrefix,
          //  是否加入时间戳
          joinTime: true,
          // 忽略重复请求
          ignoreCancelToken: true,
          // 是否携带token
          withToken: true,
          retryRequest: {
            isOpenRetry: true,
            count: 5,
            waitTime: 100
          }
        }
      },
      opt || {}
    )
  );
}
export const defHttp = createAxios();

export const originHttp = createAxios({
  requestOptions: {
    isReturnNativeResponse: true
  }
});

// koneOnline 云端调用的 http
export const koneOnlineHttp = createAxios({
  headers: {},
  requestOptions: {
    apiUrl: globSetting.koneOnlineUrl
  },
  onlineToken: true
});

// kedex 云端调用的 app
export const kedexOnlineHttp = createAxios({
  headers: {},
  requestOptions: {
    apiUrl: globSetting.kedexOnlineUrl,
    isReturnNativeResponse: false
  },
  onlineToken: true
});

export const multiMediaHttp = createAxios({
  requestOptions: {
    joinParamsToUrl: true
  }
});

export const localHttp = createAxios({
  requestOptions: {
    apiUrl: 'http://127.0.0.1:5000'
  }
});

export const ampHttp = createAxios({
  requestOptions: {
    apiUrl: '/amp-api-two'
  }
});

const isOnlineUrl = (url?: string) => {
  return url?.includes(globSetting.koneOnlineUrl) || url?.includes(globSetting.kedexOnlineUrl);
};

const isAxiosNetError = (code?: string) => {
  return code === 'ERR_NETWORK' || code === 'ETIMEDOUT';
};
